import React, { useRef, useEffect, useCallback, useState } from "react";
import { MButton } from "./MButtons";
import { IconArrowNarrowUp } from "@tabler/icons-react";

/**
 * MInfiniteScroll atom for efficient infinite scrolling lists.
 *
 * @template T - Type of each data item.
 * @param data - Array of already loaded items.
 * @param hasMore - Whether there is more data to fetch.
 * @param isLoading - Whether a fetch is in progress.
 * @param loadMore - Callback to fetch the next page of data.
 * @param renderItem - Function to render each item.
 * @param threshold - (Optional) px from bottom to trigger loadMore (default: 200).
 * @param pageSize - (Optional) number of items per page (for internal tracking).
 * @param className - (Optional) container className.
 *
 * Features:
 * - Uses IntersectionObserver for performance.
 * - Never refetches already loaded data.
 * - Shows a floating "Top" button (MButton, sm, arrow up) after scrolling down.
 * - Smooth scroll to top on button click.
 */
export interface MInfiniteScrollProps<T> {
  data: T[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  /**
   * Render function for each item. Use a unique key (e.g., item.id) for best performance.
   */
  renderItem: (item: T, index: number) => React.ReactNode;
  threshold?: number;
  className?: string;
  gap?: number;
}

function MInfiniteScroll<T>({
  data,
  hasMore,
  isLoading,
  onLoadMore,
  renderItem,
  threshold = 200,
  className = "",
  gap = 12,
}: MInfiniteScrollProps<T>) {
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showTop, setShowTop] = useState(false);
  const [lastDataLength, setLastDataLength] = useState(0);

  // Prevent double fetching
  const [fetching, setFetching] = useState(false);

  // Safe loadMore with guaranteed fetching reset
  const safeLoadMore = useCallback(async () => {
    try {
      setFetching(true);
      await onLoadMore();
    } finally {
      setFetching(false);
    }
  }, [onLoadMore]);

  // Observe scroll to bottom
  useEffect(() => {
    if (!hasMore || isLoading || fetching) return;
    const loader = loaderRef.current;
    if (!loader) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !fetching) {
          safeLoadMore();
        }
      },
      { root: null, rootMargin: `0px 0px ${threshold}px 0px`, threshold: 0.01 }
    );
    observer.observe(loader);
    return () => {
      if (loader) observer.unobserve(loader);
      observer.disconnect();
    };
  }, [hasMore, isLoading, fetching, safeLoadMore, threshold, data.length]);

  // Reset fetching after data length increases
  useEffect(() => {
    if (data.length > lastDataLength) {
      setFetching(false);
      setLastDataLength(data.length);
    }
    // eslint-disable-next-line
  }, [data.length]);

  // Show "Top" button after scrolling down
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    setShowTop(scrollTop > 300);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Scroll to top
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`minfinitescroll-container ${className}`}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap,
      }}
      tabIndex={0}
    >
      {data.map((item, idx) => (
        <React.Fragment key={idx}>{renderItem(item, idx)}</React.Fragment>
      ))}
      {/* Loader/Trigger — only mounted while there are more pages to fetch,
          so finished lists don't leave 40px of dead space below the last row. */}
      {hasMore && <div ref={loaderRef} style={{ height: 40 }} />}
      {/* {isLoading && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <span>Loading...</span>
        </div>
      )} */}
      {/* Top Button */}
      {showTop && (
        <MButton
          size="sm"
          variant="filled"
          style={{
            position: "fixed",
            right: 24,
            bottom: 48,
            zIndex: 20,
            borderRadius: 20,
            minWidth: 40,
            minHeight: 40,
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            padding: 0,
          }}
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <IconArrowNarrowUp size={20} color="#fff" stroke={2} />
        </MButton>
      )}
    </div>
  );
}

export default MInfiniteScroll;
